'use client';

/**
 * Encryption Context
 *
 * Manages client-side encryption keys for conversation history.
 *
 * Flow:
 * 1. User sets up encryption password → key bundle stored on server
 * 2. User logs in → prompted for encryption password
 * 3. Password derives KEK → unwraps data key → stored in DataKeyManager
 * 4. Data key used to encrypt/decrypt messages
 *
 * The server stores wrapped keys it cannot decrypt without the password.
 *
 * Architecture (Phase 2):
 * - DataKeyManager: Manages key lifecycle independently from React
 * - State Machine: Explicit state transitions replace boolean flags
 * - Backwards Compatible: toLegacyState() maintains existing API
 */
import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useReducer, useCallback, useEffect, useState } from 'react';
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabaseClient';
import { encrypt, decrypt, encryptJSON, decryptJSON, isEncrypted, type EncryptionKeyBundle, type RecoveryCodeBundle } from '@/lib/encryption';
import { EncryptionError } from '@/lib/encryptionErrors';
import { getDataKeyManager } from '@/lib/dataKeyManager';
import { encryptionReducer, initialEncryptionState, toLegacyState, isEncryptionReady as checkIsReady, type EncryptionState as MachineState, type EncryptionAction } from '@/lib/encryptionStateMachine';
import { getCSRFToken } from '@/hooks/useCSRF';

/**
 * Helper to get auth headers for API requests (includes CSRF token)
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: {
      session
    }
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('No active session');
  }
  const csrfToken = getCSRFToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
    ...(csrfToken && {
      'X-CSRF-Token': csrfToken
    })
  };
}

// Legacy state interface (maintained for backwards compatibility)
interface EncryptionState {
  isLoading: boolean;
  hasEncryption: boolean;
  isUnlocked: boolean;
  keyBundle: EncryptionKeyBundle | null;
  recoveryBundle: RecoveryCodeBundle | null;
  remainingRecoveryCodes: number;
}
interface EncryptionContextType {
  // State (legacy format for backwards compatibility)
  state: EncryptionState;
  isReady: boolean;

  // New: Machine state for advanced usage
  machineState: MachineState;

  // Encryption functions
  encryptText: (plaintext: string) => Promise<string>;
  decryptText: (ciphertext: string) => Promise<string>;
  encryptObject: <T>(data: T) => Promise<string>;
  decryptObject: <T>(ciphertext: string) => Promise<T>;
  isEncryptedData: (data: string) => boolean;

  // Setup and unlock functions
  refreshEncryptionState: () => Promise<void>;
  setDataKey: (key: CryptoKey) => void;
  clearDataKey: () => void;

  // Modals
  showSetupModal: boolean;
  showUnlockModal: boolean;
  setShowSetupModal: (show: boolean) => void;
  setShowUnlockModal: (show: boolean) => void;

  // Save to server
  saveEncryptionSetup: (keyBundle: EncryptionKeyBundle, recoveryBundle: RecoveryCodeBundle, deliveryMethod: string) => Promise<void>;

  // Mark recovery code as used
  markRecoveryCodeUsed: (updatedBundle: RecoveryCodeBundle, usedCodeHash: string) => Promise<void>;
}
const EncryptionContext = createContext<EncryptionContextType | undefined>(undefined);

// Get the singleton DataKeyManager
const keyManager = typeof window !== 'undefined' ? getDataKeyManager() : null;
export function EncryptionProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user,
    loadingUser
  } = useAuthSession();

  // State machine for encryption lifecycle
  const [machineState, dispatch] = useReducer(encryptionReducer, initialEncryptionState);

  // Modal visibility
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);

  // Track if key manager has a key (for state synchronization)
  const [hasKey, setHasKey] = useState(false);

  // Subscribe to DataKeyManager state changes
  useEffect(() => {
    if (!keyManager) return;
    const unsubscribe = keyManager.subscribe(keyState => {
      setHasKey(keyState.status === 'loaded');
    });
    return unsubscribe;
  }, []);

  /**
   * Fetch encryption state from server
   */
  const refreshEncryptionState = useCallback(async () => {
    if (!user?.id) {
      dispatch({
        type: 'RESET'
      });
      return;
    }
    dispatch({
      type: 'START_CHECK'
    });
    try {
      // Wait for key manager to finish restoring from session
      if (keyManager) {
        await keyManager.waitForInit();
      }

      // Get auth headers (may fail if no session yet)
      let headers: Record<string, string>;
      try {
        headers = await getAuthHeaders();
      } catch {
        // No session yet - treat as no encryption
        dispatch({
          type: 'CHECK_COMPLETE',
          hasEncryption: false
        });
        return;
      }
      const response = await fetch('/api/encryption', {
        method: 'GET',
        headers
      });

      // Handle 401 (not authenticated yet) gracefully
      if (response.status === 401) {
        dispatch({
          type: 'CHECK_COMPLETE',
          hasEncryption: false
        });
        return;
      }
      if (!response.ok) {
        throw new EncryptionError('UNKNOWN', 'Failed to fetch encryption state');
      }
      const data = await response.json();

      // Check if we already have a key from session restoration
      const keyFromSession = keyManager?.hasKey() ?? false;
      if (!data.hasEncryption) {
        dispatch({
          type: 'CHECK_COMPLETE',
          hasEncryption: false
        });
      } else if (keyFromSession) {
        // Key was restored from session, go directly to unlocked
        dispatch({
          type: 'UNLOCK_SUCCESS',
          remainingRecoveryCodes: data.recoveryCodesStatus?.remaining || 0
        });
      } else {
        // Encryption is set up but needs unlock
        dispatch({
          type: 'CHECK_COMPLETE',
          hasEncryption: true,
          keyBundle: data.keyBundle,
          recoveryBundle: data.recoveryBundle,
          remainingRecoveryCodes: data.recoveryCodesStatus?.remaining || 0
        });
        // Show unlock modal
        setShowUnlockModal(true);
      }
    } catch (err) {
      console.error('[Encryption] Failed to fetch state:', err);
      const error = err instanceof EncryptionError ? err : new EncryptionError('UNKNOWN', 'Failed to fetch encryption state');
      dispatch({
        type: 'CHECK_FAILED',
        error
      });
    }
  }, [user?.id]);

  /**
   * Initialize encryption state when user logs in
   */
  useEffect(() => {
    // Don't do anything while auth is still loading
    if (loadingUser) {
      return;
    }
    if (user?.id) {
      refreshEncryptionState();
    } else {
      // User logged out - clear everything
      keyManager?.clearKey();
      dispatch({
        type: 'RESET'
      });
    }
  }, [user?.id, loadingUser, refreshEncryptionState]);

  /**
   * Set the data key (after successful password/recovery code unlock)
   */
  const setDataKey = useCallback((key: CryptoKey) => {
    if (keyManager) {
      keyManager.setKey(key).then(() => {
        // Get remaining recovery codes from current state
        let remainingCodes = 0;
        if (machineState.type === 'locked') {
          remainingCodes = machineState.remainingRecoveryCodes;
        }
        dispatch({
          type: 'UNLOCK_SUCCESS',
          remainingRecoveryCodes: remainingCodes
        });
        setShowUnlockModal(false);
      }).catch(err_0 => {
        console.error('[Encryption] Failed to set key:', err_0);
        dispatch({
          type: 'UNLOCK_FAILED',
          error: new EncryptionError('EXPORT_FAILED', 'Failed to save encryption key')
        });
      });
    }
  }, [machineState]);

  /**
   * Clear the data key (on logout or lock)
   */
  const clearDataKey = useCallback(() => {
    keyManager?.clearKey();
    dispatch({
      type: 'LOCK'
    });
  }, []);

  /**
   * Save encryption setup to server
   */
  const saveEncryptionSetup = useCallback(async (keyBundle: EncryptionKeyBundle, recoveryBundle: RecoveryCodeBundle, deliveryMethod: string) => {
    const headers_0 = await getAuthHeaders();
    const response_0 = await fetch('/api/encryption', {
      method: 'POST',
      headers: headers_0,
      body: JSON.stringify({
        keyBundle,
        recoveryBundle,
        deliveryMethod
      })
    });
    if (!response_0.ok) {
      const error_0 = await response_0.json();
      throw new Error(error_0.error || 'Failed to save encryption setup');
    }

    // Dispatch setup complete
    dispatch({
      type: 'SETUP_COMPLETE',
      keyBundle,
      recoveryBundle
    });
  }, []);

  /**
   * Mark a recovery code as used
   */
  const markRecoveryCodeUsed = useCallback(async (updatedBundle: RecoveryCodeBundle, usedCodeHash: string) => {
    const headers_1 = await getAuthHeaders();
    const response_1 = await fetch('/api/encryption', {
      method: 'PUT',
      headers: headers_1,
      body: JSON.stringify({
        action: 'mark_code_used',
        recoveryBundle: updatedBundle,
        usedCodeHash
      })
    });
    if (!response_1.ok) {
      const error_1 = await response_1.json();
      throw new Error(error_1.error || 'Failed to update recovery codes');
    }

    // Note: State machine doesn't track recovery codes in detail
    // The unlock flow handles this
  }, []);

  /**
   * Get current key from manager
   */
  const getKey = useCallback((): CryptoKey => {
    if (!keyManager || !keyManager.hasKey()) {
      throw new EncryptionError('LOCKED', 'Encryption not unlocked. Please enter your encryption password.');
    }
    return keyManager.getKey();
  }, []);

  /**
   * Encrypt plaintext string (throws if not unlocked)
   */
  const encryptText = useCallback(async (plaintext: string): Promise<string> => {
    const key_0 = getKey();
    try {
      return await encrypt(plaintext, key_0);
    } catch (err_1) {
      throw new EncryptionError('UNKNOWN', 'Failed to encrypt data', {
        originalError: err_1 instanceof Error ? err_1 : undefined
      });
    }
  }, [getKey]);

  /**
   * Decrypt ciphertext string
   */
  const decryptText = useCallback(async (ciphertext: string): Promise<string> => {
    const key_1 = getKey();

    // If not encrypted, return as-is (backwards compatibility)
    if (!isEncrypted(ciphertext)) {
      return ciphertext;
    }
    try {
      return await decrypt(ciphertext, key_1);
    } catch (err_2) {
      const message = err_2 instanceof Error ? err_2.message.toLowerCase() : '';
      if (message.includes('tag') || message.includes('authentication')) {
        throw new EncryptionError('KEY_MISMATCH', 'Data was encrypted with a different key', {
          originalError: err_2 instanceof Error ? err_2 : undefined
        });
      }
      throw new EncryptionError('CORRUPTED_DATA', 'Failed to decrypt data - may be corrupted', {
        originalError: err_2 instanceof Error ? err_2 : undefined
      });
    }
  }, [getKey]);

  /**
   * Encrypt a JSON object
   */
  const encryptObject = useCallback(async <T,>(data_0: T): Promise<string> => {
    const key_2 = getKey();
    try {
      return await encryptJSON(data_0, key_2);
    } catch (err_3) {
      throw new EncryptionError('UNKNOWN', 'Failed to encrypt data', {
        originalError: err_3 instanceof Error ? err_3 : undefined
      });
    }
  }, [getKey]);

  /**
   * Decrypt to a JSON object
   */
  const decryptObject = useCallback(async <T,>(ciphertext_0: string): Promise<T> => {
    const key_3 = getKey();
    if (!isEncrypted(ciphertext_0)) {
      try {
        return JSON.parse(ciphertext_0);
      } catch {
        throw new EncryptionError('INVALID_FORMAT', 'Data is not valid JSON');
      }
    }
    try {
      return await decryptJSON<T>(ciphertext_0, key_3);
    } catch (err_4) {
      const message_0 = err_4 instanceof Error ? err_4.message.toLowerCase() : '';
      if (message_0.includes('tag') || message_0.includes('authentication')) {
        throw new EncryptionError('KEY_MISMATCH', 'Data was encrypted with a different key', {
          originalError: err_4 instanceof Error ? err_4 : undefined
        });
      }
      throw new EncryptionError('CORRUPTED_DATA', 'Failed to decrypt data - may be corrupted', {
        originalError: err_4 instanceof Error ? err_4 : undefined
      });
    }
  }, [getKey]);

  /**
   * Check if data is encrypted
   */
  const isEncryptedData = useCallback((data_1: string): boolean => {
    return isEncrypted(data_1);
  }, []);

  // Convert machine state to legacy format
  const legacyState = toLegacyState(machineState);

  // Override isUnlocked based on actual key presence
  const state: EncryptionState = {
    ...legacyState,
    isUnlocked: hasKey
  };

  // Determine if encryption is ready for use
  const isReady = checkIsReady(machineState) || machineState.type === 'unlocked';
  return <EncryptionContext.Provider value={{
    state,
    isReady,
    machineState,
    encryptText,
    decryptText,
    encryptObject,
    decryptObject,
    isEncryptedData,
    refreshEncryptionState,
    setDataKey,
    clearDataKey,
    showSetupModal,
    showUnlockModal,
    setShowSetupModal,
    setShowUnlockModal,
    saveEncryptionSetup,
    markRecoveryCodeUsed
  }}>
      {children}
    </EncryptionContext.Provider>;
}

/**
 * Hook to access encryption functions.
 * Returns a "not ready" state if used outside EncryptionProvider.
 */
export function useEncryption() {
  const $ = _c(2);
  const context = useContext(EncryptionContext);
  if (context === undefined) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        isLoading: true,
        hasEncryption: false,
        isUnlocked: false,
        keyBundle: null,
        recoveryBundle: null,
        remainingRecoveryCodes: 0
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        state: t0,
        isReady: false,
        machineState: {
          type: "uninitialized"
        },
        encryptText: _temp,
        decryptText: _temp2,
        encryptObject: _temp3,
        decryptObject: _temp4,
        isEncryptedData: _temp5,
        refreshEncryptionState: _temp6,
        setDataKey: _temp7,
        clearDataKey: _temp8,
        showSetupModal: false,
        showUnlockModal: false,
        setShowSetupModal: _temp9,
        setShowUnlockModal: _temp10,
        saveEncryptionSetup: _temp11,
        markRecoveryCodeUsed: _temp12
      };
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  return context;
}
async function _temp12() {}
async function _temp11() {}
function _temp10() {}
function _temp9() {}
function _temp8() {}
function _temp7() {}
async function _temp6() {}
function _temp5() {
  return false;
}
async function _temp4() {
  throw new Error("Encryption not available - EncryptionProvider not found");
}
async function _temp3() {
  throw new Error("Encryption not available - EncryptionProvider not found");
}
async function _temp2() {
  throw new Error("Encryption not available - EncryptionProvider not found");
}
async function _temp() {
  throw new Error("Encryption not available - EncryptionProvider not found");
}